import { supabase } from "./supabase";
import type { CoravuCall, CoravuCallStatus } from "../types/database";

export type CoravuCallOutcome = "sent_program" | "followup_needed" | "not_a_fit" | "ghosted";

export type CoravuCallRow = CoravuCall & {
  company_name: string | null;
  related_program_name: string | null;
  outcome?: CoravuCallOutcome | null;
};

type RawRow = CoravuCall & {
  companies: { name: string } | { name: string }[] | null;
  competitions: { name: string } | { name: string }[] | null;
  outcome?: CoravuCallOutcome | null;
};

function unwrapOne<T extends object>(rel: T | T[] | null): T | null {
  if (rel == null) return null;
  return Array.isArray(rel) ? (rel[0] ?? null) : rel;
}

function expand(row: RawRow): CoravuCallRow {
  const company = unwrapOne(row.companies);
  const program = unwrapOne(row.competitions);
  return {
    ...row,
    company_name: company?.name ?? null,
    related_program_name: program?.name ?? null,
  };
}

const SELECT =
  "id, company_id, attendee_name, attendee_email, starts_at, ends_at, teams_join_url, ics_url, status, notes, outcome, related_program_id, created_at, updated_at, companies(name), competitions(name)";

/**
 * Upcoming calls: scheduled status AND starting today or later.
 * Ordered ascending so the soonest call sits at the top.
 */
export async function listUpcomingCalls(): Promise<CoravuCallRow[]> {
  const dayStart = new Date();
  dayStart.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from("coravu_calls")
    .select(SELECT)
    .eq("status", "scheduled")
    .gte("starts_at", dayStart.toISOString())
    .order("starts_at", { ascending: true });
  if (error || !data) return [];
  return (data as unknown as RawRow[]).map(expand);
}

/**
 * Past / closed calls: anything completed, cancelled, no-show, or whose
 * starts_at is already in the past (regardless of status).
 */
export async function listPastCalls(limit = 50): Promise<CoravuCallRow[]> {
  const dayStart = new Date();
  dayStart.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from("coravu_calls")
    .select(SELECT)
    .or(
      `status.in.(completed,cancelled,no_show),starts_at.lt.${dayStart.toISOString()}`
    )
    .order("starts_at", { ascending: false })
    .limit(limit);
  if (error || !data) return [];
  return (data as unknown as RawRow[]).map(expand);
}

export type CreateCallInput = {
  attendee_name: string;
  attendee_email: string;
  starts_at: string;
  ends_at?: string | null;
  company_id?: string | null;
  teams_join_url?: string | null;
  ics_url?: string | null;
  notes?: string | null;
  outcome?: CoravuCallOutcome | null;
  related_program_id?: string | null;
};

export async function createCall(input: CreateCallInput): Promise<CoravuCallRow | null> {
  const { data, error } = await supabase
    .from("coravu_calls")
    .insert({
      attendee_name: input.attendee_name.trim(),
      attendee_email: input.attendee_email.trim().toLowerCase(),
      starts_at: input.starts_at,
      ends_at: input.ends_at ?? null,
      company_id: input.company_id ?? null,
      teams_join_url: input.teams_join_url ?? null,
      ics_url: input.ics_url ?? null,
      notes: input.notes ?? null,
      related_program_id: input.related_program_id ?? null,
    })
    .select(SELECT)
    .single();
  if (error || !data) return null;
  return expand(data as unknown as RawRow);
}

export async function updateCallStatus(
  id: string,
  status: CoravuCallStatus
): Promise<boolean> {
  const { error } = await supabase
    .from("coravu_calls")
    .update({ status, updated_at: new Date().toISOString() })
    .eq("id", id);
  return !error;
}

export async function linkCallToProgram(
  id: string,
  programId: string | null
): Promise<boolean> {
  const { error } = await supabase
    .from("coravu_calls")
    .update({ related_program_id: programId, updated_at: new Date().toISOString() })
    .eq("id", id);
  return !error;
}

export async function updateCall(
  id: string,
  patch: Partial<CreateCallInput>
): Promise<boolean> {
  const { error } = await supabase
    .from("coravu_calls")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  return !error;
}

// ─── Day grouping helpers (UI only) ────────────────────────────────────

export type DayBucket = "today" | "tomorrow" | "this_week" | "later";

export function bucketForDate(d: Date, now: Date = new Date()): DayBucket {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const tomorrow = new Date(start);
  tomorrow.setDate(start.getDate() + 1);
  const dayAfter = new Date(start);
  dayAfter.setDate(start.getDate() + 2);
  const weekEnd = new Date(start);
  weekEnd.setDate(start.getDate() + 7);
  if (d >= start && d < tomorrow) return "today";
  if (d >= tomorrow && d < dayAfter) return "tomorrow";
  if (d >= dayAfter && d < weekEnd) return "this_week";
  return "later";
}
